"use client";

import Link from "next/link";
import type { UserSummary } from "@theta/api-client";
import { formatCount } from "@/lib/counts";
import { profileHref } from "@/lib/profileHref";
import { ProfileHoverCard } from "./ProfileHoverCard";
import { Avatar } from "./ui/Avatar";
import { cn } from "./ui/cn";

/**
 * A ROW OF OVERLAPPING FACES, WITH A "+N" — one implementation
 *
 * The group header's member row and a poll option's voter row were the same
 * fifteen lines twice: a `Link` per person to `/profil/{id}`, an `Avatar` at
 * `sm` inside a `border-2 border-surface` ring, a hover tooltip carrying the
 * name, and a remainder count beside them. This is that, once.
 *
 * THE HOVER IS `ProfileHoverCard` (OSO-397), which is why the tooltip is gone
 * rather than kept beside it. Both copies used to draw a name in a black box;
 * the shared card is what the post author and the comment author already open,
 * and it carries the name plus the bio, the role in this group, "medlem siden"
 * and "Send besked" — along with the open/close delays, the portal, the
 * viewport-aware positioning and the Escape handler that a hand-rolled tooltip
 * has none of. One extraction, and both surfaces gain it.
 *
 * A BOUNDED ARRAY PLUS THE TRUE TOTAL (D9), never a page. `people` is the
 * prefix the wire carried — eight members, ten voters — and `total` is what
 * the whole set actually numbers, so "+N" is arithmetic on two numbers the
 * caller already has and never a second request. A caller with no larger set
 * behind the row just omits `total`.
 *
 * IT IS `components/`, NOT `components/ui/`. Every entry in `ui/` is
 * presentation-only; this one knows that a person has a profile page, where
 * that page lives and what their profile card looks like, which makes it app
 * furniture in the same way `AvatarStack` — a DIFFERENT idiom, the DM
 * participant clump, with no links, no cards and no remainder — is.
 */
export function Facepile({
  people,
  total,
  groupId,
  className,
}: {
  people: UserSummary[];
  /**
   * How many there are in all — the group's member count, the option's vote
   * count. Defaults to `people.length`, i.e. "this is everybody".
   */
  total?: number;
  /**
   * The group these faces are being shown IN, if any. It only makes the hover
   * card contextual — the role badge, "medlem af gruppen siden" and the
   * affiliation chip are all facts about a group — so a facepile with no group
   * behind it still opens a card; it just says less.
   */
  groupId?: number;
  className?: string;
}) {
  if (people.length === 0) return null;

  /**
   * `profileHref`, not a template literal: ruling O-21a is stated in ONE
   * module (`lib/profileHref.ts`) and this is a link site like the nineteen
   * others, not a new rule. It returns `null` for anyone there is no profile
   * to link to — the `id: 0` placeholder the event serializer substitutes for
   * a deleted creator is already on the wire — and the one thing the module
   * forbids is inventing a fallback address.
   *
   * SO THE FILTER RUNS BEFORE THE ARITHMETIC. "+N" is the remainder against
   * what actually got DRAWN, not against what was handed in; counting a
   * person who was then dropped makes them vanish from both halves at once
   * and leaves the row silently one short.
   */
  const shown = people.flatMap((person) => {
    const href = profileHref(person.id);
    return href == null ? [] : [{ person, href }];
  });
  const rest = (total ?? people.length) - shown.length;

  return (
    /**
     * `relative`, and it is load-bearing rather than tidiness: a poll option
     * draws this row over an `absolute inset-y-0` progress fill, and an
     * in-flow "+N" with no positioning of its own paints UNDERNEATH that fill.
     */
    <div className={cn("relative flex items-center", className)}>
      <div className="flex -space-x-2">
        {shown.map(({ person, href }) => (
          <ProfileHoverCard key={person.id} userId={person.id} categoryId={groupId}>
            <Link href={href} aria-label={person.name ?? ""}>
              <div className="border-2 border-surface rounded-full">
                <Avatar url={person.avatar.small} name={person.name ?? ""} size="sm" />
              </div>
            </Link>
          </ProfileHoverCard>
        ))}
      </div>
      {rest > 0 && (
        <span className="ml-2 text-xs text-muted-foreground tabular-nums">
          +{formatCount(rest)}
        </span>
      )}
    </div>
  );
}
